import { ReactNode } from 'react'

import {
  Direction,
  TypeVariationProps,
  VariationReverse,
} from '#/components/FormatCell/Types/Variation/types'
import Colors from '#/styles/Colors'

const resolveReverse = (reverse?: VariationReverse) =>
  typeof reverse === 'object'
    ? { color: !!reverse.color, icon: !!reverse.icon }
    : { color: !!reverse, icon: !!reverse }

const resolveLevelColor = (level: TypeVariationProps['level'], value: ReactNode) => {
  const resolvedLevel = typeof level === 'function' ? level(value) : level

  return resolvedLevel ? Colors.feedback[resolvedLevel]?.color : undefined
}

/**
 * Converte o `value` em número quando for `number` ou string numérica;
 * `reverse` inverte o sinal (para métricas onde queda é melhora).
 */
const resolveSignedNumber = (value: ReactNode, reverse?: boolean): number | null => {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null
  }

  if (typeof value === 'string' && value.trim() === '') {
    return null
  }

  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) {
    return null
  }

  return reverse ? -numericValue : numericValue
}

/** Cor automática por sinal: positivo → melhora (verde), negativo → piora (vermelho). */
const resolveSignColor = (value: ReactNode, reverse?: boolean) => {
  const signedNumber = resolveSignedNumber(value, reverse)

  if (signedNumber === null || signedNumber === 0) {
    return undefined
  }

  return signedNumber > 0 ? Colors.variation.improve.color : Colors.variation.deteriorate.color
}

export const resolveValueColor = ({
  level,
  reverse,
  value,
}: Pick<TypeVariationProps, 'level' | 'reverse' | 'value'>) =>
  resolveLevelColor(level, value) ?? resolveSignColor(value, resolveReverse(reverse).color)

/** Direção da seta pelo sinal do valor; `reverse.icon` inverte a direção. */
export const resolveDirection = (
  value: ReactNode,
  reverse?: VariationReverse,
): Direction | null => {
  const signedNumber = resolveSignedNumber(value, resolveReverse(reverse).icon)

  if (signedNumber === null || signedNumber === 0) {
    return null
  }

  return signedNumber > 0 ? 'up' : 'down'
}
